import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { Player } from 'modules/players/domain/interfaces/player';
import { deleteTournamentUser } from 'modules/players/store/actions';
import { ListPlayersArgs, useListPlayersQuery } from 'modules/players/store/players-api';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePagedList } from 'shared/hooks/paged-list';
import { AppDispatch } from 'store/store';

/** Ключ localStorage для `perPage`: размер страницы запоминается между визитами. */
const STORAGE_KEY = 'players';
const DEFAULT_PER_PAGE = 10;

/**
 * Стабильная ссылка обязательна: `usePagedList` считает по аргументам ключ скоупа,
 * а безымянная стрелка меняла бы идентичность на каждом рендере.
 */
const mapSearchToArgs = (search: string): Partial<ListPlayersArgs> => ({ nameFilter: search || undefined });

/**
 * Список игроков организации: страница, размер страницы и поиск живут в URL,
 * данные — в RTK Query.
 *
 * Раньше страница копила игроков в redux-слайсе: «следующие» дописывались в общий
 * массив по `offset: players.length`, а поиск жил во втором массиве рядом. Из-за
 * этого `?page=7` на холодный старт откатывался на первую страницу, а созданный
 * игрок мог сдвинуть смещение и потеряться. Теперь запрашивается ровно одна
 * страница, а число страниц выводится приёмом `limit + 1` (см. `usePagedList`).
 */
export const usePlayerList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const baseArgs = useMemo(() => ({ tournamentId: Number(tournamentId) }), [tournamentId]);

  const list = usePagedList<Player, ListPlayersArgs>({
    useQueryHook: useListPlayersQuery,
    baseArgs,
    mapSearchToArgs,
    storageKey: STORAGE_KEY,
    defaultPerPage: DEFAULT_PER_PAGE,
    // Пока организация неизвестна, запрос ушёл бы за чужими игроками.
    skip: !tournamentId,
    isPending: !tournamentId,
  });

  const deletePlayer = useCallback(
    (userId: number) => {
      // Мутация осталась танком; список обновит инвалидация тега внутри неё.
      return dispatch(deleteTournamentUser({ userId })).unwrap();
    },
    [dispatch],
  );

  return { ...list, deletePlayer };
};
